"use client";

import { useState, type CSSProperties } from "react";

import cn from "classnames";

import { Link } from "@/components/link/link.component";

import { resolveLinkHref } from "@/utils/url.util";

import { brown } from "@/app/font";
import type { GetHomePageQueryResult } from "@/sanity.types";

export const AdtProfessionalMonitoring = ({
  block,
}: {
  block: ProfessionalMonitoringBlock;
}) => {
  const benefits = (block.benefits ?? []).filter(
    (benefit): benefit is ProfessionalMonitoringBenefit & { _key: string } =>
      Boolean(benefit?._key && benefit.title),
  );

  const [activeKey, setActiveKey] = useState<string | null>(null);

  if (!block.heading || benefits.length === 0) return null;

  const titleId = `professional-monitoring-${block._key}`;
  const desktopSrc = block.image?.url;
  const mobileSrc = block.mobileImage?.url || desktopSrc;
  const alt = block.image?.alt || block.mobileImage?.alt || "";
  const href = resolveLinkHref(block.cta?.link);
  const showCta = Boolean(block.cta?.label && href && href !== "#");

  const activeBenefit =
    benefits.find((benefit) => benefit._key === activeKey) ?? null;
  const eyebrow = activeBenefit?.eyebrow || block.eyebrow;
  const heading =
    activeBenefit?.heading || activeBenefit?.title || block.heading;
  const body = activeBenefit?.body || activeBenefit?.description || block.body;

  return (
    <section
      data-testid="adt-professional-monitoring"
      aria-labelledby={titleId}
      className="w-full bg-white py-10 xl:py-12">
      <div className="container mx-auto w-full px-4">
        <div className="bg-blu-ink overflow-hidden rounded-[24px] text-white">
          <div
            data-testid="adt-professional-monitoring-media"
            className="relative min-h-[20rem] lg:min-h-[400px] xl:min-h-[420px]">
            {mobileSrc ? (
              <picture className="absolute inset-0">
                {desktopSrc && desktopSrc !== mobileSrc ? (
                  <source media="(min-width: 1024px)" srcSet={desktopSrc} />
                ) : null}
                <img
                  src={mobileSrc}
                  alt={alt}
                  className="h-full w-full object-cover [object-position:var(--pm-mobile-pos)] lg:[object-position:var(--pm-desktop-pos)]"
                  style={
                    {
                      "--pm-mobile-pos": focalPoint(
                        block.mobileImage ?? block.image,
                      ),
                      "--pm-desktop-pos": focalPoint(block.image),
                    } as CSSProperties
                  }
                />
              </picture>
            ) : null}
            <div
              aria-hidden
              className="from-blu-ink via-blu-ink/80 to-blu-ink/40 pointer-events-none absolute inset-0 bg-linear-to-r lg:via-45% lg:to-transparent"
            />
            <div className="relative z-10 flex min-h-[20rem] flex-col justify-start px-6 py-10 sm:px-8 lg:min-h-[400px] lg:justify-center lg:px-12 lg:py-16 xl:min-h-[420px]">
              {eyebrow ? (
                <p
                  className={cn(
                    brown.className,
                    "text-primary max-w-[28rem] text-[13px] font-bold tracking-[0.12em] uppercase sm:text-sm lg:max-w-[35rem]",
                  )}>
                  {eyebrow}
                </p>
              ) : null}
              <h2
                id={titleId}
                className={cn(
                  brown.className,
                  "mt-3 max-w-[28rem] text-[32px] leading-[1.08] font-bold tracking-tight text-white sm:text-[40px] lg:max-w-[30.5rem] lg:text-5xl",
                )}>
                {heading}
              </h2>
              {body ? (
                <p className="mt-3 max-w-[28rem] text-base leading-snug lg:max-w-105">
                  {body}
                </p>
              ) : null}
              {showCta ? (
                <Link
                  href={href}
                  target={block.cta?.link?.openInNewTab ? "_blank" : undefined}
                  rel={
                    block.cta?.link?.openInNewTab
                      ? "noopener noreferrer"
                      : undefined
                  }
                  className={cn(
                    brown.className,
                    "border-primary text-primary focus-visible:ring-primary focus-visible:ring-offset-blu-ink mt-8 inline-flex min-h-12 w-fit max-w-full items-center justify-center rounded-full border-2 px-7 py-3 text-center text-base leading-snug font-bold whitespace-normal focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
                  )}>
                  {block.cta?.label}
                </Link>
              ) : null}
            </div>
          </div>

          <ul
            id="adt-professional-monitoring-benefits"
            data-testid="adt-professional-monitoring-benefits"
            className={cn(
              "grid grid-cols-1 border-t border-white/15",
              benefits.length > 1 && "lg:divide-x lg:divide-white/15",
              {
                "lg:grid-cols-2": benefits.length === 2,
                "lg:grid-cols-3": benefits.length >= 3,
              },
            )}>
            {benefits.map((benefit) => {
              const isActive = benefit._key === activeKey;

              return (
                <li
                  key={benefit._key}
                  tabIndex={0}
                  aria-current={isActive ? "true" : undefined}
                  onMouseEnter={() => setActiveKey(benefit._key)}
                  onFocus={() => setActiveKey(benefit._key)}
                  className="group min-w-0 focus-visible:outline-none">
                  <div
                    className={cn(
                      "flex flex-col items-start gap-4 rounded-2xl border-2 border-transparent px-6 py-8 transition-[border-color,background-color,color] duration-300 sm:px-8 lg:gap-5 lg:py-10",
                      "group-hover:border-primary group-focus-visible:border-primary h-full",
                      isActive && "border-primary",
                    )}>
                    {benefit.icon?.url ? (
                      <span
                        className={cn(
                          "border-primary group-hover:bg-primary group-focus-visible:bg-primary flex size-14 shrink-0 items-center justify-center rounded-full border-[1.5px] bg-transparent transition-colors duration-300",
                          isActive && "bg-primary",
                        )}>
                        <span
                          aria-hidden
                          className="bg-primary group-hover:bg-secondary group-focus-visible:bg-secondary size-7 transition-colors duration-300"
                          style={{
                            maskImage: `url(${benefit.icon.url})`,
                            WebkitMaskImage: `url(${benefit.icon.url})`,
                            maskSize: "contain",
                            maskRepeat: "no-repeat",
                            maskPosition: "center",
                          }}
                        />
                      </span>
                    ) : null}
                    <div className="flex min-w-0 flex-col gap-2">
                      <h3
                        className={cn(
                          brown.className,
                          "text-[22px] leading-[1.15] font-bold text-white lg:text-[32px]",
                        )}>
                        {benefit.title}
                      </h3>
                      {benefit.description ? (
                        <p className="text-lg leading-snug">
                          {benefit.description}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
};

function focalPoint(
  image?: {
    hotspot?: { x?: number | null; y?: number | null } | null;
  } | null,
) {
  const x = image?.hotspot?.x;
  const y = image?.hotspot?.y;
  if (typeof x !== "number" || typeof y !== "number") return "center";
  const percent = (value: number) => `${Number((value * 100).toFixed(2))}%`;
  return `${percent(x)} ${percent(y)}`;
}

export type ProfessionalMonitoringBlock = Extract<
  NonNullable<NonNullable<GetHomePageQueryResult>["pageBuilder"]>[number],
  { _type: "adtProfessionalMonitoring" }
>;

type ProfessionalMonitoringBenefit = NonNullable<
  NonNullable<ProfessionalMonitoringBlock["benefits"]>[number]
>;
